<template>
	<div
		class="checkbox checkbox--only"
		:class="{
			...{
				'is-dark': theme === 'dark',
				'checkbox--mobile': isMobileWebApp,
				[sizeClass]: true,
				neutral: neutral,
			},
			...classes,
		}"
	>
		<input
			:id="id"
			v-model="internalValue"
			:disabled="disabled"
			type="checkbox"
			@change="handleChange"
		>
		<label
			:for="id"
			:class="sizeClass"
		>
			<Label-With-Tooltip
				v-if="label"
				:label="label"
				:ref-element="'checkbox__label'"
			>
				<div
					ref="checkbox__label"
					class="checkbox__label"
				>
					{{ label }}
				</div>
			</Label-With-Tooltip>
		</label>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name:  'Checkbox',
	props: {
		theme: {
			type:    String,
			default: '',
		},
		id: {
			type:    [Number, String],
			default: '',
		},
		label: {
			type:    String,
			default: '',
		},
		value: {
			type: Boolean,
		},
		classes: {
			type: Object,
		},
		disabled: {
			type: Boolean,
		},
		size: {
			type:    String,
			default: 'normal',
		},
		neutral: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			internalValue: false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		sizeClass() {
			switch (this.size) {
			case 'small':
				return 'checkbox--size-small';
			case 'normal':
			default:
				return 'checkbox--size-normal';
			}
		},
	},
	watch: {
		value(value) {
			this.internalValue = value;
		},
	},
	mounted() {
		this.internalValue = this.value;
	},
	methods: {
		handleChange(event) {
			const value = event.target.checked;
			this.$emit('input', value);
			this.$emit('change', value);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.checkbox {
	box-sizing: border-box;
}

.checkbox + .checkbox {
	margin-top: 24px;
}

.checkbox.add-space {
	margin-bottom: 24px;
}

.checkbox p {
	@include text-md-1;
	line-height: 24px;
	margin-top: 5px;
	margin-bottom: 0;
	padding-left: 36px;
}

.checkbox [type='checkbox']:not(:checked),
.checkbox [type='checkbox']:checked {
	position: absolute;
	left: -9999px;
	opacity: 0;
}

.checkbox [type='checkbox'] + label {
	font-weight: 400;
	font-size: inherit;
	line-height: inherit;
	position: relative;
	cursor: pointer;
	margin-bottom: 0;
	padding-left: 36px;
	color: var(--color-grey-300);
	user-select: none;
	backface-visibility: hidden;
}

.checkbox [type='checkbox']:checked + label {
	color: var(--color-soft-white);
}

.checkbox [type='checkbox'] + label::before,
.checkbox [type='checkbox'] + label::after {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 0;
	content: '';
}

.checkbox [type='checkbox'] + label::before {
	transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
	border: 1px solid var(--color-grey-300);
	border-radius: 0;
}

.checkbox.checkbox--size-normal {
	height: 22px;
}

.checkbox.checkbox--size-small {
	height: 16px;
}

.checkbox [type='checkbox'] + label.checkbox--size-normal::before {
	width: 22px;
	height: 22px;
}

.checkbox [type='checkbox'] + label.checkbox--size-normal::after {
	width: 22px;
	height: auto;
	line-height: 22px;
}

.checkbox [type='checkbox'] + label.checkbox--size-small::before {
	width: 16px;
	height: 16px;
}

.checkbox [type='checkbox'] + label.checkbox--size-small::after {
	width: 16px;
	height: auto;
	line-height: 19px;
	font-size: 16px;
}

.checkbox [type='checkbox'] + label::after {
	@include sdx-icons;
	transform: scale(0);
	transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
	transform-origin: 50% 50%;
	text-align: center;
	color: var(--color-text-on-hype-yellow);
	font-size: 20px;
	content: '\E00B';
	cursor: pointer;
}

.checkbox.neutral [type='checkbox'] + label::after {
	content: '\E034';
	transform: scale(1);
	background-color: var(--color-hype-yellow);
}

.checkbox [type='checkbox']:checked + label::before {
	border: 0;
	background: var(--color-hype-yellow);
}

.checkbox [type='checkbox']:checked + label::after {
	color: var(--color-text-on-hype-yellow);
	transform: scale(1);
}

.checkbox [type='checkbox']:disabled + label,
.checkbox [type='checkbox']:disabled + label + p {
	opacity: 0.4;
	pointer-events: none;
}

.checkbox--only [type='checkbox'] + label {
	display: inline-block;
	padding-left: 30px;
	height: 22px;
}

.is-dark.checkbox [type='checkbox'] + label {
	font-weight: 400;
	line-height: 18px;
	letter-spacing: -0.1px;
	font-size: 14px;
	position: relative;
	cursor: pointer;
	margin-bottom: 0;
	padding-left: 36px;
	color: var(--color-soft-white);
	user-select: none;
	backface-visibility: hidden;
}

// .checkbox [type='checkbox']:hover + label::before {
	// border: 1px solid var(--color-grey-600);
// }

.checkbox [type='checkbox']:checked:hover + label::before {
	border-color: var(--color-hype-yellow);
	// background: var(--color-grey-600);
}

.is-dark.checkbox--only [type='checkbox'] + label {
	display: inline-block;
	padding-left: 18px;
	height: 18px;
}

.checkbox__label {
	min-width: 100%;
}
</style>
